import { Client, Room, SeatReservation } from 'colyseus.js';
import { ClientClockSync } from './clock-sync';
import type { ModelInstance } from '../render/instanced-layout';

/**
 * Connects to the live PvP Arena room (backend
 * src/game/pvp-arena/rooms/arena.room.ts). Movement/combat sends
 * (sendMove/sendAttack/sendParry/sendJump/sendSprint/sendCrouch/sendBlock)
 * are reused as-is from colyseus-client.ts — ArenaRoom speaks the identical
 * input.* protocol Dungeon Instance does, minus feint/ability/lantern
 * (design/gdd/pvp-arena.md's disclosed scope cuts).
 */

const COLYSEUS_URL = import.meta.env.VITE_COLYSEUS_URL || `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/colyseus`;

export interface ArenaLayoutPayload {
  instances: ModelInstance[];
  /** design/gdd/pvp-arena.md Core Rule #3 — one entry per team, each a list of userIds. Used client-side only to label round-over results. */
  teams: string[][];
}

export interface ArenaJoinResult {
  room: Room;
  clockSync: ClientClockSync;
  layout: ArenaLayoutPayload;
}

async function afterJoin(room: Room): Promise<ArenaJoinResult> {
  const clockSync = new ClientClockSync(room);
  clockSync.start();

  const layout = await new Promise<ArenaLayoutPayload>((resolve) => {
    room.onMessage('arena.layout', (msg: ArenaLayoutPayload) => resolve(msg));
    room.send('request.layout');
  });

  return { room, clockSync, layout };
}

/** design/gdd/sconce-hub.md Core Rule #5 pattern — the real production path, via Matchmaking's `matchmaking.found` seat reservation. */
export async function joinArenaViaReservation(reservation: SeatReservation): Promise<ArenaJoinResult> {
  const client = new Client(COLYSEUS_URL);
  const room = await client.consumeSeatReservation(reservation);
  return afterJoin(room);
}
